import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ACTION_STATUS_LABELS,
  phaseIndex,
  PHASE_LABELS,
  TOTAL_STEPS,
  type ActionItemView,
  type ActionStatus,
} from '@retro/shared';
import { ChevronRight, Plus } from 'lucide-react';
import clsx from 'clsx';
import { api, type RetroSummary } from '../lib/api.js';
import { useCurrentUser } from '../lib/auth.js';
import { useTeams } from '../lib/teams.js';
import { toast } from '../lib/toast.js';
import { AppShell, useLiveRetro } from '../components/AppShell.js';
import { EmptyState, Modal, Stat } from '../components/ui.js';

const DATE_EYEBROW = new Intl.DateTimeFormat('en-GB', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

function greeting(hour: number): string {
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

function dueLabel(action: ActionItemView): string | null {
  if (!action.dueDate) return null;
  return `Due ${new Date(action.dueDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;
}

export function DashboardPage() {
  const { data: user } = useCurrentUser();
  const { teams, activeTeam, isLoading: teamsLoading } = useTeams();
  const queryClient = useQueryClient();

  const retrosQuery = useQuery({
    queryKey: ['retros', activeTeam?.id],
    queryFn: () => api.teams.retros(activeTeam!.id),
    enabled: Boolean(activeTeam),
  });
  const actionsQuery = useQuery({
    queryKey: ['team-actions', activeTeam?.id],
    queryFn: () => api.teams.actions(activeTeam!.id),
    enabled: Boolean(activeTeam),
  });
  const liveRetro = useLiveRetro(activeTeam);

  const [creatingTeam, setCreatingTeam] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');

  const createTeam = useMutation({
    mutationFn: (name: string) => api.teams.create(name),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['teams'] });
      setCreatingTeam(false);
      setNewTeamName('');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const setStatus = useMutation({
    mutationFn: ({ actionItemId, status }: { actionItemId: string; status: ActionStatus }) =>
      api.teams.setActionStatus(activeTeam!.id, actionItemId, status),
    onMutate: async ({ actionItemId, status }) => {
      const key = ['team-actions', activeTeam?.id];
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<{ actionItems: ActionItemView[] }>(key);
      queryClient.setQueryData<{ actionItems: ActionItemView[] }>(key, (current) =>
        current
          ? {
              actionItems: current.actionItems.map((item) =>
                item.id === actionItemId ? { ...item, status } : item,
              ),
            }
          : current,
      );
      return { previous };
    },
    onError: (error: Error, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['team-actions', activeTeam?.id], context.previous);
      }
      toast.error(error.message);
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['team-actions', activeTeam?.id] }),
  });

  const stats = useMemo(() => {
    const actions = actionsQuery.data?.actionItems ?? [];
    const retros = retrosQuery.data?.retros ?? [];
    const open = actions.filter((a) => a.status !== 'DONE');
    const weekAhead = Date.now() + 7 * 86_400_000;
    const dueThisWeek = open.filter((a) => a.dueDate && Date.parse(a.dueDate) <= weekAhead).length;
    const ninetyDaysAgo = Date.now() - 90 * 86_400_000;
    const recent = actions.filter((a) => Date.parse(a.createdAt) >= ninetyDaysAgo);
    const doneRecent = recent.filter((a) => a.status === 'DONE').length;
    const lastRetro = retros
      .filter((r): r is RetroSummary => r.phase === 'CLOSED')
      .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))[0];
    const openSessions = new Set(open.map((a) => a.retroId)).size;
    return { open, dueThisWeek, lastRetro, completion: recent.length ? Math.round((doneRecent / recent.length) * 100) : null, openSessions };
  }, [actionsQuery.data, retrosQuery.data]);

  const myActions = useMemo(
    () =>
      (actionsQuery.data?.actionItems ?? [])
        .filter((a) => a.assignee?.userId === user?.id)
        .sort((a, b) => (a.status === 'DONE' ? 1 : 0) - (b.status === 'DONE' ? 1 : 0)),
    [actionsQuery.data, user?.id],
  );

  const recentRetros = useMemo(
    () =>
      (retrosQuery.data?.retros ?? [])
        .slice()
        .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
        .slice(0, 6),
    [retrosQuery.data],
  );

  if (!user) return null;

  const firstName = user.name.split(' ')[0];
  const liveStep = liveRetro ? phaseIndex(liveRetro.phase) + 1 : null;
  const canFacilitate = activeTeam?.role === 'FACILITATOR';

  return (
    <AppShell user={user} crumb="Overview">
      <div className="ph">
        <div>
          <span className="eyebrow">{DATE_EYEBROW.format(new Date())}</span>
          <h2>
            {greeting(new Date().getHours())}, {firstName}
          </h2>
          <p className="lede">
            {activeTeam ? `What needs your attention in ${activeTeam.name} today.` : 'What needs your attention today.'}
          </p>
        </div>
        {canFacilitate && activeTeam ? (
          <div className="ph-actions">
            <Link to={`/teams/${activeTeam.id}/formats`} className="btn btn-primary">
              <Plus size={15} aria-hidden /> Start retrospective
            </Link>
            {user.isAdmin ? (
              <button type="button" className="btn" onClick={() => setCreatingTeam(true)}>
                <Plus size={15} aria-hidden /> Create team
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      {liveRetro ? (
        <div className="live">
          <div>
            <span className="live-dot">
              <span className="relative flex size-2">
                <span className="absolute inline-flex size-2 animate-ping rounded-full bg-[#12b76a] opacity-75" />
                <span className="relative inline-flex size-2 rounded-full bg-[#12b76a]" />
              </span>
              Live now · {liveRetro.participantCount} in session
            </span>
            <h3>{liveRetro.name}</h3>
            <p className="sub">
              Step {liveStep} of {TOTAL_STEPS} · {PHASE_LABELS[liveRetro.phase]}
              {canFacilitate ? ' · you are facilitating' : ''}
            </p>
            <span className="meter">
              <span style={{ width: `${((liveStep ?? 1) / TOTAL_STEPS) * 100}%` }} />
            </span>
          </div>
          <Link to={`/retro/${liveRetro.id}`} className="btn btn-light">
            Rejoin session <ChevronRight size={15} aria-hidden />
          </Link>
        </div>
      ) : null}

      <div className="stats">
        <Stat
          label="Open team actions"
          value={stats.open.length}
          note={
            stats.dueThisWeek
              ? `${stats.dueThisWeek} due this week`
              : stats.openSessions
                ? `across ${stats.openSessions} session${stats.openSessions === 1 ? '' : 's'}`
                : undefined
          }
        />
        <Stat
          label="Last retrospective"
          value={
            stats.lastRetro
              ? new Date(stats.lastRetro.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
              : '—'
          }
          note={stats.lastRetro ? `${stats.lastRetro.name} · ${stats.lastRetro.participantCount} people` : 'None yet'}
        />
        <Stat
          label="Actions completed"
          value={stats.completion === null ? '—' : `${stats.completion}%`}
          note="last 90 days"
        />
      </div>

      <div className="two mt-[26px]">
        <section>
          <div className="sec-h">
            <div>
              <h3>Assigned to you</h3>
              <p>Tick an action when it is complete.</p>
            </div>
            {activeTeam ? (
              <Link to={`/teams/${activeTeam.id}`} className="link link-plain">
                Action board <ChevronRight size={13} className="inline" aria-hidden />
              </Link>
            ) : null}
          </div>
          <div className="card">
            {myActions.length === 0 ? (
              <p className="empty text-muted">Nothing assigned to you right now.</p>
            ) : (
              <ul className="list">
                {myActions.map((action) => {
                  const done = action.status === 'DONE';
                  return (
                    <li key={action.id} className="li">
                      <button
                        type="button"
                        role="checkbox"
                        aria-checked={done}
                        aria-label={`Mark “${action.title}” ${done ? 'not done' : 'done'}`}
                        onClick={() =>
                          setStatus.mutate({
                            actionItemId: action.id,
                            status: done ? 'OPEN' : 'DONE',
                          })
                        }
                        className={clsx(
                          'grid size-[22px] flex-none place-items-center rounded-md border text-white',
                          done ? 'border-brand-500 bg-brand-500' : 'border-line2 bg-white',
                        )}
                      >
                        {done ? '✓' : ''}
                      </button>
                      <span className="li-main">
                        <strong className={done ? 'strike' : undefined}>{action.title}</strong>
                        <small>
                          {[dueLabel(action), `from ${action.retroName}`].filter(Boolean).join(' · ')}
                        </small>
                      </span>
                      <span
                        className={clsx(
                          'badge',
                          action.status === 'DONE' && 'badge-ok',
                          action.status === 'IN_PROGRESS' && 'badge-warn',
                          action.status === 'OPEN' && 'badge-accent',
                        )}
                      >
                        {ACTION_STATUS_LABELS[action.status]}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>


        <section>
          <div className="sec-h">
            <div>
              <h3>Recent sessions</h3>
              <p>Summaries stay available to the team.</p>
            </div>
          </div>
          <div className="card">
            {recentRetros.length === 0 ? (
              <p className="empty text-muted">No sessions yet.</p>
            ) : (
              <ul className="list">
                {recentRetros.map((retro) => (
                  <li key={retro.id} className="li">
                    <span className="li-main">
                      <strong>{retro.name}</strong>
                      <small>
                        {new Date(retro.createdAt).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                        {' · '}
                        {retro.templateName}
                        {' · '}
                        {retro.participantCount} people
                      </small>
                    </span>
                    <div className="flex flex-none items-center gap-2">
                      <span
                        className={clsx(
                          'badge',
                          retro.phase === 'CLOSED' ? 'badge-ok' : 'badge-accent',
                        )}
                      >
                        {retro.phase === 'CLOSED' ? 'Done' : PHASE_LABELS[retro.phase]}
                      </span>
                      <Link to={`/retro/${retro.id}`} className="btn btn-sm">
                        {retro.phase === 'CLOSED' ? 'View summary' : 'Rejoin'}
                      </Link>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>

      {teams.length === 0 && !teamsLoading ? (
        <div className="card mt-6">
          {user.isAdmin ? (
            <>
              <EmptyState emoji="👋" title="No teams yet">
                Retrospectives run inside a team — create the first one, then share its invite link.
              </EmptyState>
              <div className="px-5 pb-5 text-center">
                <button type="button" className="btn btn-primary" onClick={() => setCreatingTeam(true)}>
                  Create a team
                </button>
              </div>
            </>
          ) : (
            <EmptyState emoji="👋" title="You are not in a team yet">
              Retrospectives run inside a team. Ask an administrator to create one and send you its invite link.
            </EmptyState>
          )}
        </div>
      ) : null}

      <Modal
        open={user.isAdmin && creatingTeam}
        onClose={() => setCreatingTeam(false)}
        title="Create a team"
        width="sm"
        footer={
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setCreatingTeam(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!newTeamName.trim() || createTeam.isPending}
              onClick={() => createTeam.mutate(newTeamName.trim())}
            >
              Create
            </button>
          </>
        }
      >
        <p className="mb-3">
          Separate teams keep retrospective history private to the people in them.
        </p>
        <input
          className="input"
          value={newTeamName}
          onChange={(event) => setNewTeamName(event.target.value)}
          placeholder="Platform squad"
          maxLength={80}
          aria-label="Team name"
        />
      </Modal>
    </AppShell>
  );
}
